import React from 'react'
import styled from '@emotion/styled'
import { useSelector } from 'react-redux'
import { Icon } from '@mobi/component-library/Common/V2/Icon'
import { layering, spacing, radius, font, colors } from '@mobi/component-library/Theme/Common'
import { getIsAppHeaderVisible } from '@core/Areas/AppHeader/Store/selectors'
import { ProfileAvatar } from '@core/Areas/FollowMe/Components/Common/ProfileAvatar'
import type { FollowMeSharedBet } from '@core/Areas/FollowMe/types'

const enum LocalConstants {
  AppHeaderIsVisibleClass = 'follow-me__new-posts--shift',
  AvatarWrapperClass = 'follow-me__new-posts__avatars',
}

export const NewPostsIndicator: React.FC<{
  newPosts: FollowMeSharedBet[]
  onClick: () => void
}> = ({ newPosts, onClick }) => {
  const isAppHeaderVisible = useSelector(getIsAppHeaderVisible)
  const shouldDisplay = newPosts.length > 0

  return (
    <WrapperStyled className={isAppHeaderVisible ? LocalConstants.AppHeaderIsVisibleClass : ''}>
      <button onClick={onClick} type='button' hidden={!shouldDisplay}>
        See new tips
        <span className={LocalConstants.AvatarWrapperClass}>
          {newPosts.slice(0, 3).map(post => (
            <ProfileAvatar
              key={post.postId}
              size='xs'
              color={post.ownerAccountColor}
              avatar={post.ownerAccountAvatar}
            />
          ))}
        </span>
        <Icon name='LineArrowUp' size='2rem' />
      </button>
    </WrapperStyled>
  )
}

// ======
// Styles
// ======

const WrapperStyled = styled.div({
  position: 'sticky',
  zIndex: layering.overlayHigh,
  top: '2rem',
  display: 'flex',
  justifyContent: 'center',
  marginTop: `-${spacing.smx1}`,

  [`&.${LocalConstants.AppHeaderIsVisibleClass}`]: { top: '6.6rem' },

  '> button': {
    position: 'absolute',
    display: 'flex',
    gap: spacing.smx2,
    justifyContent: 'center',
    alignItems: 'center',
    width: 'fit-content',
    padding: `${spacing.smx1} ${spacing.smx1}  ${spacing.smx1} ${spacing.sm} `,
    background: colors.studio[500],
    border: 0,
    borderRadius: radius.lgx3,
    color: colors.white,
    cursor: 'pointer',
    fontFamily: font.family.primary,
    fontSize: font.size.lg.fontSize,
    letterSpacing: font.size.lg.letterSpacing,
    lineHeight: font.size.lg.lineHeight,
    fontWeight: font.weight.medium,
    touchAction: 'manipulation',
    WebkitTapHighlightColor: 'transparent',
    userSelect: 'none',

    transition: 'opacity 0.3s ease-in, translate 0.3s ease-in, display 0.3s ease-in',
    transitionBehavior: 'allow-discrete',

    '@starting-style': {
      opacity: 0.9,
      translate: '0 -100%',
    },

    '&[hidden]': {
      opacity: 0.9,
      translate: '0 -100%',
      display: 'none !important',
      transitionDuration: '.2s',
      transitionTimingFunction: 'cubic-bezier(0,0,0,1)',
    },

    [`.${LocalConstants.AvatarWrapperClass}`]: {
      display: 'flex',

      '> *': {
        outline: '0.2rem solid ' + colors.studio[500],
        marginLeft: '-0.8rem',

        '&:first-of-type': { marginLeft: 0 },
      },
    },
  },
})
